import { Box, Button, Chip, CircularProgress, Divider, TextField, Typography, useTheme } from "@mui/material";
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import QrCodeScannerOutlinedIcon from '@mui/icons-material/QrCodeScannerOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { useContext, useEffect, useState } from "react";
import QRCode from "react-qr-code";
import Fetch from "../../services/Fetch";
import AuthContext from "../../context/AuthContext";
import SnackbarAlert from "../../components/SnackBar";
import useSnackBar from "../../hooks/UseSnackBar";
import DashboardStatCard from "../../components/cards/DashboardStatCard";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import ScrollReveal from "../../components/motion/ScrollReveal";
import PageHero from "../../components/motion/PageHero";
import { useConstants } from "../../hooks/UseConstants";
import QrScanner from "../../components/QrScanner";
import { formatCurrency } from "../../helper/formatCurrency";

function parseChargeToken(raw) {
    const value = String(raw ?? '').trim();
    if (!value) return '';

    const uuidLike = value.match(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i);
    if (uuidLike) return uuidLike[0];

    const lastPathSegment = value.match(/wallet\/charge\/([^/?#]+)/i);
    if (lastPathSegment?.[1]) return decodeURIComponent(lastPathSegment[1]);

    const tokenParam = value.match(/[?&]token=([^&#]+)/i);
    if (tokenParam?.[1]) return decodeURIComponent(tokenParam[1]);

    return value;
}

function formatDate(value, isAr) {
    if (!value) return isAr ? 'غير متوفر' : 'Unavailable';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);

    return new Intl.DateTimeFormat(isAr ? 'ar-EG' : 'en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(parsed);
}

function Wallet() {
    const theme = useTheme();
    const { host, language } = useConstants();
    const { user, refreshUser } = useContext(AuthContext);
    const isAr = language === 'ar';
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const [token, setToken] = useState('');
    const [loading, setLoading] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [txLoading, setTxLoading] = useState(true);
    const [scanOpen, setScanOpen] = useState(false);

    const studentQr = user?.id ? `student:${user.id}` : '';

    const loadTx = async () => {
        setTxLoading(true);
        const result = await Fetch(`${host}/transaction/history?per_page=20`, 'GET');
        if (result.status === 200) {
            const payload = result.data?.data;
            const list = Array.isArray(payload) ? payload : (payload?.data || []);
            setTransactions(Array.isArray(list) ? list : []);
        }
        setTxLoading(false);
    };

    const balance = user?.wallet?.balance ?? 0;

    const getTransactionLabel = (tx) => {
        if (tx.description) return tx.description;
        if (tx.type === 'credit') return isAr ? 'إيداع / شحن محفظة' : 'Wallet top-up';
        if (tx.type === 'withdraw') return isAr ? 'سحب' : 'Withdrawal';
        if (tx.type === 'debit') return isAr ? 'خصم / شراء' : 'Purchase';
        return isAr ? 'معاملة' : 'Transaction';
    };

    const getTransactionColor = (type) => {
        if (type === 'credit') return { color: 'success.main', chip: 'success' };
        if (type === 'withdraw') return { color: 'warning.main', chip: 'warning' };
        if (type === 'debit') return { color: 'error.main', chip: 'error' };
        return { color: 'text.primary', chip: 'default' };
    };

    useEffect(() => { loadTx(); }, [host]);

    const charge = async (rawToken) => {
        const parsedToken = parseChargeToken(rawToken ?? token);
        if (!parsedToken.trim()) {
            setSnackBar('error', isAr ? 'أدخل رمز الشحن من الأدمن' : 'Enter the admin top-up token');
            return;
        }

        setLoading(true);
        const result = await Fetch(`${host}/student/wallet/charge/${encodeURIComponent(parsedToken.trim())}`, 'POST', {});
        if (result.status === 200) {
            setSnackBar('success', isAr
                ? `تم شحن ${formatCurrency(result.data.data?.amount ?? 0, true)} بنجاح`
                : `${formatCurrency(result.data.data?.amount ?? 0, false)} added successfully`);
            setToken('');
            await refreshUser();
            await loadTx();
        } else {
            setSnackBar('error', result.data?.errors?.[0] || result.data?.message || (isAr ? 'فشل الشحن' : 'Top-up failed'));
        }
        setLoading(false);
    };

    return (
        <Box>
            <PageHero
                title={isAr ? 'المحفظة' : 'Wallet'}
                subtitle={isAr ? `رصيدك الحالي: ${formatCurrency(balance, true)}` : `Current balance: ${formatCurrency(balance, false)}`}
                icon={<AccountBalanceWalletIcon />}
            />

            <ScrollReveal delay={60}>
            <Box className="max-w-md mb-4">
                <DashboardStatCard
                    label={isAr ? 'الرصيد الحالي' : 'Current balance'}
                    value={formatCurrency(balance, isAr)}
                    icon={<AccountBalanceWalletIcon />}
                    className="w-full mx-0"
                />
            </Box>
            </ScrollReveal>

            {studentQr && (
                <ScrollReveal delay={100}>
                <DashboardPanelCard
                    title={isAr ? 'رمز QR للتعريف' : 'Student identification QR'}
                    subtitle={isAr ? 'اعرضه للأدمن عند الحاجة لتجهيز شحن المحفظة.' : 'Show this to the admin when preparing a wallet top-up.'}
                    className="mx-0 mb-4"
                >
                    <Box sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'inline-block', p: 1, bgcolor: '#fff', borderRadius: 2 }}>
                            <QRCode value={studentQr} size={140} />
                        </Box>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                            {isAr ? 'معرف الحساب' : 'Account ID'}: {user.id} — {user.first_name} {user.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                            {isAr
                                ? 'اعرض الرمز للأدمن عند الشحن، ثم امسح أو أدخل رمز الشحن أدناه.'
                                : 'Show this QR to the admin, then scan or enter the top-up token below.'}
                        </Typography>
                    </Box>
                </DashboardPanelCard>
                </ScrollReveal>
            )}

            <ScrollReveal delay={140}>
            <DashboardPanelCard
                title={isAr ? 'شحن برمز من الأدمن' : 'Top up with admin token'}
                subtitle={isAr ? 'امسح رمز QR أو الصق الرابط أو التوكن ليتم شحن المحفظة.' : 'Scan the QR code or paste the link/token to add funds to your wallet.'}
                className="mx-0 mb-4"
            >
                {scanOpen ? (
                    <Box sx={{ mb: 2 }}>
                        <QrScanner
                            title={isAr ? 'امسح رمز شحن الأدمن' : 'Scan admin top-up QR'}
                            onScan={async (decoded) => {
                                const parsed = parseChargeToken(decoded);
                                setToken(parsed);
                                setScanOpen(false);
                                await charge(parsed);
                            }}
                            onClose={() => setScanOpen(false)}
                        />
                    </Box>
                ) : (
                    <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<QrCodeScannerOutlinedIcon />}
                        onClick={() => setScanOpen(true)}
                        sx={{ mb: 2, py: 1.2, borderRadius: 2, fontWeight: 700 }}
                    >
                        {isAr ? 'مسح QR الشحن بالكاميرا' : 'Scan top-up QR with camera'}
                    </Button>
                )}

                <TextField
                    fullWidth
                    size="small"
                    label={isAr ? 'رمز الشحن أو رابط QR' : 'Top-up token or QR link'}
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    sx={{ mb: 1.5 }}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    {isAr
                        ? 'يمكنك مسح رمز الأدمن مباشرة أو لصق الرابط/الرمز وسيتم استخراج التوكن تلقائياً.'
                        : 'Scan the admin QR directly or paste the link/token and it will be extracted automatically.'}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Button
                    variant="contained"
                    fullWidth
                    onClick={() => charge()}
                    disabled={loading}
                    sx={{ bgcolor: '#0D358C', '&:hover': { bgcolor: '#1d4ed8' } }}
                >
                    {loading ? <CircularProgress size={22} color="inherit" /> : (isAr ? 'تأكيد الشحن' : 'Confirm top-up')}
                </Button>
            </DashboardPanelCard>
            </ScrollReveal>

            <ScrollReveal delay={180}>
            <DashboardPanelCard
                title={isAr ? 'سجل المعاملات' : 'Transaction history'}
                subtitle={isAr ? 'آخر عمليات الشحن والخصم المرتبطة بمحفظتك.' : 'Recent top-ups and debits tied to your wallet.'}
                className="mx-0"
            >
                <Box>
                    {txLoading ? (
                        <CircularProgress size={28} />
                    ) : transactions.length === 0 ? (
                        <Box
                            sx={{
                                py: 4,
                                textAlign: 'center',
                                borderRadius: 3,
                                border: '1px dashed',
                                borderColor: theme.palette.mode === 'dark' ? 'divider' : '#cbd5e1',
                            }}
                        >
                            <ReceiptLongOutlinedIcon sx={{ fontSize: 34, color: 'text.secondary', mb: 1 }} />
                            <Typography color="text.secondary">
                                {isAr ? 'لا توجد معاملات حتى الآن.' : 'No transactions yet.'}
                            </Typography>
                        </Box>
                    ) : (
                        transactions.map((tx, i) => (
                            <Box
                                key={tx.id || i}
                                sx={{
                                    py: 1.75,
                                    borderBottom: '1px solid',
                                    borderColor: theme.palette.mode === 'dark' ? 'divider' : '#e5e7eb',
                                    '&:last-of-type': { borderBottom: 'none', pb: 0 },
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={700}>
                                            {getTransactionLabel(tx)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDate(tx.created_at, isAr)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                        <Chip
                                            size="small"
                                            label={getTransactionLabel({ type: tx.type, description: '' })}
                                            color={getTransactionColor(tx.type).chip}
                                            variant="outlined"
                                        />
                                        <Typography variant="body2" fontWeight={800} sx={{ color: getTransactionColor(tx.type).color }}>
                                            {formatCurrency(tx.amount, isAr)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))
                    )}
                </Box>
            </DashboardPanelCard>
            </ScrollReveal>

            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </Box>
    );
}

export default Wallet;
